import React, { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Button, Form, Grid, Message, Segment } from 'semantic-ui-react';
import { useAuth } from '../context/AuthContext';
import { stockyApi } from '../misc/StockyApi';
import { handleLogError } from '../misc/Helpers';
import {
  buildCreateBillingPayload,
  buildOwnerOptions,
  marketplaceCountryOptions,
  normalizePhoneNumber,
  validateBillingFields,
  validateEmail,
} from '../users/userForm';

const roleOptions = [
  { key: 'user', text: 'Usuario', value: 'USER' },
  { key: 'admin', text: 'Admin', value: 'ADMIN' },
];

function Signup() {
  const Auth = useAuth();
  const isLoggedIn = Auth.userIsAuthenticated();
  const userAuthenticated = Auth.getUser();
  const isAdmin = Auth.isAdmin();

  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState('USER');
  const [searchParams] = useSearchParams();
  // "Agregar cuenta hija" in the users table opens this form with the owner already chosen.
  const [ownerUsername, setOwnerUsername] = useState(() => searchParams.get('owner') || '');
  const [ownerOptions, setOwnerOptions] = useState(null);
  const [marketplaceCountry, setMarketplaceCountry] = useState('AR');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [nextPaymentAmount, setNextPaymentAmount] = useState('');
  const [nextPaymentDueDate, setNextPaymentDueDate] = useState('');
  const isEmployee = ownerUsername.trim() !== '';
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [wasEmployee, setWasEmployee] = useState(false);

  useEffect(() => {
    if (!isLoggedIn || !isAdmin || !userAuthenticated) return;
    let cancelled = false;
    stockyApi
      .getUsers(userAuthenticated)
      .then((response) => {
        if (!cancelled) setOwnerOptions(buildOwnerOptions(Array.isArray(response?.data) ? response.data : []));
      })
      .catch(() => {
        // Without the list the field falls back to typing the owner username.
        if (!cancelled) setOwnerOptions(null);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, isAdmin]);

  const clearMessages = () => {
    setIsError(false);
    setErrorMessage('');
    setIsSuccess(false);
  };

  const clearForm = () => {
    setUsername('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setRole('USER');
    setOwnerUsername('');
    setMarketplaceCountry('AR');
    setPhoneNumber('');
    setNextPaymentAmount('');
    setNextPaymentDueDate('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearMessages();

    if (!username || !email || !password || !confirmPassword) {
      setIsError(true);
      setErrorMessage('Completa todos los campos.');
      return;
    }

    if (password !== confirmPassword) {
      setIsError(true);
      setErrorMessage('Las contrasenas no coinciden.');
      return;
    }

    const validationError =
      validateEmail(email) ||
      (isEmployee ? null : validateBillingFields({ phoneNumber, nextPaymentAmount, nextPaymentDueDate }));
    if (validationError) {
      setIsError(true);
      setErrorMessage(validationError);
      return;
    }

    try {
      const billing = isEmployee
        ? {}
        : buildCreateBillingPayload({ phoneNumber, nextPaymentAmount, nextPaymentDueDate });
      await stockyApi.signup(
        {
          username,
          email,
          password,
          role: isEmployee ? 'USER' : role,
          marketplaceCountry,
          ownerUsername: ownerUsername.trim() || undefined,
          ...billing,
        },
        userAuthenticated,
      );
      setWasEmployee(isEmployee);
      clearForm();
      setIsSuccess(true);
    } catch (error) {
      handleLogError(error);
      const backendData = error?.response?.data;
      const message = Array.isArray(backendData?.message)
        ? backendData.message.join(' ')
        : backendData?.message || 'No se pudo crear el usuario.';
      setIsError(true);
      setErrorMessage(message);
    }
  };

  if (!isLoggedIn || !isAdmin) {
    return <Navigate to="/inicio" />;
  }

  return (
    <Grid textAlign="center" style={{ marginTop: 30 }}>
      <Grid.Column style={{ maxWidth: 520 }}>
        <Form size="large" onSubmit={handleSubmit}>
          <Segment>
            <Form.Input
              fluid
              autoFocus
              name="username"
              icon="user"
              iconPosition="left"
              placeholder="Username"
              value={username}
              onChange={(_, { value }) => setUsername(value)}
            />
            <Form.Input
              fluid
              name="email"
              icon="at"
              iconPosition="left"
              placeholder="Email"
              value={email}
              onChange={(_, { value }) => setEmail(value)}
            />
            <Form.Input
              fluid
              name="password"
              icon="lock"
              iconPosition="left"
              placeholder="Password"
              type="password"
              value={password}
              onChange={(_, { value }) => setPassword(value)}
            />
            <Form.Input
              fluid
              name="confirmPassword"
              icon="lock"
              iconPosition="left"
              placeholder="Confirmar Password"
              type="password"
              value={confirmPassword}
              onChange={(_, { value }) => setConfirmPassword(value)}
            />
            <Form.Select
              fluid
              label="Rol"
              options={roleOptions}
              value={role}
              onChange={(_, { value }) => setRole(String(value || 'USER'))}
              disabled={isEmployee}
            />
            <Form.Select
              fluid
              label="País de Mercado Libre"
              options={marketplaceCountryOptions}
              value={marketplaceCountry}
              onChange={(_, { value }) => setMarketplaceCountry(String(value || 'AR'))}
              disabled={isEmployee}
            />
            {ownerOptions ? (
              <Form.Select
                fluid
                search
                clearable
                name="ownerUsername"
                label="Cuenta dueña (para crear una cuenta hija)"
                placeholder="Ninguna: es una cuenta principal"
                options={ownerOptions}
                value={ownerUsername || undefined}
                onChange={(_, { value }) => setOwnerUsername(String(value || ''))}
              />
            ) : (
              <Form.Input
                fluid
                name="ownerUsername"
                icon="users"
                iconPosition="left"
                label="Cuenta dueña (para crear una cuenta hija)"
                placeholder="Opcional: usuario de la cuenta dueña"
                value={ownerUsername}
                onChange={(_, { value }) => setOwnerUsername(value)}
              />
            )}
            {isEmployee ? (
              <Message info size="small">
                Esta será una cuenta hija de {ownerUsername}: trabaja con los datos de esa cuenta, siempre con rol
                Usuario y sin datos de pago propios (el pago pertenece a la cuenta dueña).
              </Message>
            ) : (
              <>
                <Form.Input
                  fluid
                  name="phoneNumber"
                  icon="whatsapp"
                  iconPosition="left"
                  placeholder="Teléfono de WhatsApp (opcional, ej: +54 9 11 6793-4361)"
                  value={phoneNumber}
                  onChange={(_, { value }) => setPhoneNumber(value)}
                  onBlur={(_, { value }) => setPhoneNumber(normalizePhoneNumber(value))}
                />
                <Form.Group widths="equal">
                  <Form.Input
                    fluid
                    label="Monto a pagar (opcional)"
                    name="nextPaymentAmount"
                    type="number"
                    min="0"
                    step="0.01"
                    value={nextPaymentAmount}
                    onChange={(_, { value }) => setNextPaymentAmount(value)}
                  />
                  <Form.Input
                    fluid
                    label="Vencimiento (opcional)"
                    name="nextPaymentDueDate"
                    type="date"
                    value={nextPaymentDueDate}
                    onChange={(_, { value }) => setNextPaymentDueDate(value)}
                  />
                </Form.Group>
              </>
            )}
            <Button color="purple" fluid size="large">
              Crear Usuario
            </Button>
          </Segment>
        </Form>

        {isError ? <Message negative>{errorMessage}</Message> : null}
        {isSuccess ? <Message positive>{wasEmployee ? 'Cuenta hija creada correctamente.' : 'Usuario creado correctamente.'}</Message> : null}
      </Grid.Column>
    </Grid>
  );
}

export default Signup;
